import React, { useState, useRef, useEffect } from 'react';
import Calendar from '../../calendar.js';
import InputWrapper from './InputWrapper';

const ExpirationMonthYearInput = ({ 
  field, 
  fieldId, 
  value, 
  onChange, 
  label, 
  isRequired, 
  error, 
  placeholder,
  language,
  iconInsideInput = true
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef(null);
  const expirationValue = (() => {
    if (value && typeof value === 'object' && 'month' in value && 'year' in value) {
      return { month: value.month ?? '', year: value.year ?? '' };
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const trimmed = value.trim();
      // Support legacy "YYYY-MM" format
      if (trimmed.includes('-') && !trimmed.includes('/')) {
        const [y, m] = trimmed.split('-');
        return { month: m ? String(m).padStart(2, '0') : '', year: y || '' };
      }
      // Support "MM/DD/YYYY HH:mm:ss" format (e.g. "06/01/2026 00:00:00")
      const dateTimeMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
      if (dateTimeMatch) {
        const [, mm, , yyyy] = dateTimeMatch;
        return { month: mm, year: yyyy };
      }
      return { month: '', year: '' };
    }
    return { month: '', year: '' };
  })();
  
  // Format display value - default to number format (12/2025), optional text format (December 2025)
  const showMonthAsText = field.showMonthAsText || false;
  const expirationDisplayValue = (() => {
    if (!expirationValue.month || !expirationValue.year) return '';
    if (showMonthAsText) {
      const monthIndex = parseInt(expirationValue.month) - 1;
      const monthNames = language === 'fr' 
        ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
        : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      return monthIndex >= 0 && monthIndex < 12 
        ? `${monthNames[monthIndex]} ${expirationValue.year}`
        : `${expirationValue.month}/${expirationValue.year}`;
    } else {
      // Default: number format (MM/YYYY)
      return `${expirationValue.month}/${expirationValue.year}`;
    }
  })();
  
  const now = new Date();
  const defaultMinDate = new Date(now.getFullYear(), now.getMonth(), 1);
  defaultMinDate.setHours(0, 0, 0, 0);
  const defaultMaxDate = new Date(now.getFullYear() + 10, 11, 31);
  defaultMaxDate.setHours(23, 59, 59, 999);

  // Determine the calendar's initial month - use stored value or current month, never before current month
  const calendarInitialMonth = (() => {
    if (expirationValue.month && expirationValue.year) {
      const d = new Date(parseInt(expirationValue.year), parseInt(expirationValue.month) - 1, 1);
      return d < defaultMinDate ? new Date(defaultMinDate.getTime()) : d;
    }
    return new Date(defaultMinDate.getTime());
  })();

  const toggleCalendar = () => {
    setShowCalendar(prev => !prev);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };
  
  const handleExpirationDateSelect = (selectedDate) => {
    if (selectedDate) {
      const minDate = field.min
        ? (() => { const d = new Date(field.min); d.setHours(0, 0, 0, 0); return d; })()
        : defaultMinDate;
      if (selectedDate < minDate) return;
      const maxDate = field.max
        ? (() => { const d = new Date(field.max); d.setHours(23, 59, 59, 999); return d; })()
        : defaultMaxDate;
      if (selectedDate > maxDate) return;
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear().toString();
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const formattedExpiration = `${month}/${day}/${year} 00:00:00`;
      onChange(fieldId, formattedExpiration);
      closeCalendar();
    }
  };

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        // Check if click is not on the input or calendar icon
        const inputElement = event.target.closest('.formulaire-dynamique-expiration-month-year-input-container');
        if (!inputElement || !inputElement.contains(event.target)) {
          closeCalendar();
        }
      }
    };

    if (showCalendar) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCalendar]);

  const containerClassName = iconInsideInput 
    ? "formulaire-dynamique-expiration-month-year-input-container formulaire-dynamique-expiration-month-year-input-icon-inside"
    : "formulaire-dynamique-expiration-month-year-input-container";
  
  const inputClassName = iconInsideInput
    ? "profile-input clickable form-input formulaire-dynamique-expiration-month-year-input-input"
    : "profile-input clickable form-input";
  
  const iconClassName = iconInsideInput
    ? "profile-img formulaire-dynamique-expiration-month-year-input-icon-inside"
    : "profile-img";

  return (
    <InputWrapper field={field} label={label} isRequired={isRequired} error={error}>
      <div className={containerClassName}>
        <input
          type="text"
          className={inputClassName}
          value={expirationDisplayValue || ''}
          readOnly={true}
          onMouseDown={e => toggleCalendar()}
          placeholder={placeholder || 'MM/YYYY'}
          disabled={field.readOnly || false}
        />
        <img
          src={require('../../../../images/icons/calendar-2.png')}
          alt="calendrier"
          className={iconClassName}
          onMouseDown={e => toggleCalendar()}
        />
        {showCalendar && (
          <div
            className="calendar-wrapper"
            ref={calendarRef}
            tabIndex={0}
          >
            <Calendar
              onDayClick={handleExpirationDateSelect}
              initialMonth={calendarInitialMonth}
              minDate={field.min ? new Date(field.min) : defaultMinDate}
              maxDate={field.max ? new Date(field.max) : defaultMaxDate}
              allowChangeYear={true}
              monthsAndYearsOnly={true}
            />
          </div>
        )}
      </div>
    </InputWrapper>
  );
};

export default ExpirationMonthYearInput;

